import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    ArrowRight, Server as ServerIcon, Camera, Loader2, Play, X, Check,
    Globe, Database, Home, Lock, Cog, Clock, Package, FileText,
    AlertTriangle, Power, Shield, Terminal, Folder, Settings2,
    Activity, Layers, CheckCheck, Zap,
} from "lucide-react";
import { http, errMsg, formatBytes, formatDuration } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import SearchInput, { filterByName, useUrlSearchState } from "@/components/SearchInput";
import PageHero from "@/components/PageHero";
import RecentMigrationsPulse, { adaptServerJobs } from "@/components/RecentMigrationsPulse";

/**
 * MigrateServer — server-to-server migration page.
 *
 * UX matches StorageMigrationsNAS.jsx (NAS engine): a landing list of
 * past server migrations + a "New Server Migration" CTA that opens a
 * compact dialog wizard (source / destination / OS / role / scope /
 * advanced). Submitting still POSTs the legacy <c>/api/jobs</c> body
 * (server-flavoured) — visual redesign only, backing engine unchanged.
 *
 * Active view delegates to <c>/jobs/:id</c> (existing detail page).
 */

// Scope catalog. ``os`` = which target_os values show the row.
// ``default`` = checked when the user arrives.
const SCOPE_ITEMS = [
    { v: "system_config",    label: "System Config",    icon: Cog,       default: true,  sizeGB: 1,  os: ["linux", "windows"], note: "/etc · Windows system config. Service definitions, package state." },
    { v: "web_root",         label: "Web Root",         icon: Globe,     default: true,  sizeGB: 12, os: ["linux", "windows"], note: "/var/www · /srv/www · C:\\inetpub\\wwwroot" },
    { v: "app_data",         label: "Application Data", icon: Package,   default: true,  sizeGB: 25, os: ["linux", "windows"], note: "/opt · /srv · C:\\Program Files app state" },
    { v: "user_homes",       label: "User Homes",       icon: Home,      default: true,  sizeGB: 40, os: ["linux", "windows"], note: "/home · C:\\Users — skip if PC migration handles this." },
    { v: "ssl_certs",        label: "SSL Certs",        icon: Lock,      default: true,  sizeGB: 1,  os: ["linux", "windows"], note: "/etc/ssl · letsencrypt · Windows cert store" },
    { v: "database_backups", label: "DB Backups",       icon: Database,  default: false, sizeGB: 15, os: ["linux", "windows"], note: "Pre-baked dumps. Raw DB dirs should not be copied live." },
    { v: "systemd_services", label: "systemd Services", icon: Power,     default: true,  sizeGB: 0,  os: ["linux"],            note: "/etc/systemd/system unit files." },
    { v: "cron_jobs",        label: "Cron Jobs",        icon: Clock,     default: false, sizeGB: 0,  os: ["linux"],            note: "/etc/cron.* and /var/spool/cron." },
    { v: "docker_volumes",   label: "Docker Volumes",   icon: Folder,    default: false, sizeGB: 50, os: ["linux", "windows"], advanced: true, warn: "Stop containers before syncing." },
    { v: "logs",             label: "Logs",             icon: FileText,  default: false, sizeGB: 8,  os: ["linux", "windows"], advanced: true, note: "Usually not migrated." },
];

const DEFAULT_SCOPE = SCOPE_ITEMS.filter((s) => s.default).map((s) => s.v);

const ROLES = [
    { v: "web",            label: "Web",            icon: Globe,    note: "Nginx · Apache · IIS" },
    { v: "database",       label: "Database",       icon: Database, note: "PostgreSQL · MySQL · MongoDB" },
    { v: "application",    label: "Application",    icon: Terminal, note: "Node · Python · Java" },
    { v: "infrastructure", label: "Infrastructure", icon: Shield,   note: "DNS · VPN · Monitoring" },
    { v: "custom",         label: "Custom",         icon: Settings2, note: "Mixed workload" },
];

const SERVER_KINDS = new Set(["server", "vm"]);

export default function MigrateServer() {
    return <LandingView />;
}

// ---- Landing — list of past + new-migration CTA -----------------------------

function LandingView() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [search, setSearch] = useUrlSearchState("q");

    const refresh = useCallback(async () => {
        try {
            // Reuse existing /api/jobs — filter to server-kind jobs
            // client-side. Backend already serializes the option blob.
            const { data } = await http.get("/jobs");
            setJobs((data || []).filter((j) =>
                (j.migration_kind === "server") ||
                (j.server_options && Object.keys(j.server_options).length)
            ));
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const stats = useMemo(() => {
        const total = jobs.length;
        const running = jobs.filter((j) =>
            ["queued", "running", "syncing", "transferring"].includes((j.status || "").toLowerCase())
        ).length;
        const done = jobs.filter((j) =>
            ["completed", "verified"].includes((j.status || "").toLowerCase())
        ).length;
        return { total, running, done };
    }, [jobs]);

    // Search filter — matches name + source / destination agent names + id.
    // Stats stay computed off the full set so the headline counters never
    // shift when the operator narrows the view.
    const visibleJobs = useMemo(
        () => filterByName(jobs, search, ["name", "source_agent_name", "dest_agent_name", "id"]),
        [jobs, search],
    );

    return (
        <div className="mx-auto max-w-[1400px] space-y-5 p-6">
            <PageHero
                eyebrow="Server-to-Server"
                title="Server Migration"
                description="Move a Linux or Windows Server in one step — config, services, data, certs. Service-aware: agents stop / restart matching daemons around the copy."
                icon={ServerIcon}
                tint="#00C2FF"
                testId="srv-page-hero"
                actions={
                    <Button
                        onClick={() => setShowNew(true)}
                        data-testid="srv-new-migration-btn"
                        className="bg-[#5b5ef0] hover:bg-[#4b4ef0]"
                    >
                        <Camera size={14} className="mr-2" /> New Server Migration
                    </Button>
                }
            />

            {/* Metric cards (mirrors NAS engine dashboard layout) */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Metric icon={Layers}   label="Migrations" value={stats.total.toLocaleString()} />
                <Metric icon={Activity} label="In flight"  value={stats.running.toLocaleString()} accent="blue" />
                <Metric icon={CheckCheck} label="Completed" value={stats.done.toLocaleString()} accent="emerald" />
                <Metric icon={Zap}      label="Engine"     value="rsync+delta" sublabel="block-level diff" />
            </div>

            <RecentMigrationsPulse items={adaptServerJobs(jobs)} testId="srv-recent-pulse" />

            {jobs.length > 0 && (
                <div className="mb-4 flex items-center justify-between gap-3">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        placeholder="Search server migrations by name or agent…"
                        testid="srv-search-input"
                        className="w-full max-w-sm"
                    />
                    <div
                        className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
                        data-testid="srv-search-count"
                    >
                        {visibleJobs.length} of {jobs.length}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="rounded-lg border border-border bg-surface p-10 text-center text-sm text-muted-foreground">
                    <Loader2 size={16} className="mr-2 inline animate-spin" /> Loading server migrations…
                </div>
            ) : jobs.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-surface p-10 text-center text-sm text-muted-foreground">
                    No server migrations yet. Click "New Server Migration" to start one.
                </div>
            ) : visibleJobs.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-surface p-10 text-center text-sm text-muted-foreground" data-testid="srv-no-search-results">
                    No server migrations match <span className="font-mono text-foreground">{search}</span>.
                </div>
            ) : (
                <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
                    <table className="w-full text-sm" data-testid="srv-migrations-table">
                        <thead className="border-b border-border bg-background/40">
                            <tr className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                <th className="px-4 py-2 text-left">Name</th>
                                <th className="px-4 py-2 text-left">Source → Destination</th>
                                <th className="px-4 py-2 text-left">Status</th>
                                <th className="px-4 py-2 text-left">Created</th>
                                <th className="px-4 py-2 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleJobs.map((j) => (
                                <tr key={j.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/30">
                                    <td className="px-4 py-2.5 font-medium">{j.name || j.id.slice(0, 8)}</td>
                                    <td className="px-4 py-2.5 text-xs">
                                        {j.source_agent_name || j.source_agent_id?.slice(0, 8)}
                                        <ArrowRight size={10} className="mx-1 inline" />
                                        {j.destination_agent_name || j.dest_agent_id?.slice(0, 8) || j.destination_agent_id?.slice(0, 8)}
                                    </td>
                                    <td className="px-4 py-2.5"><StatusPill status={j.status} /></td>
                                    <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                                        {(j.created_at || "").replace("T", " ").slice(0, 19)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <Link
                                            to={`/jobs/${j.id}`}
                                            data-testid={`srv-open-${j.id}`}
                                            className="text-xs text-[#5b5ef0] hover:text-[#4b4ef0]"
                                        >
                                            Open →
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showNew && (
                <NewServerMigrationDialog
                    onClose={() => setShowNew(false)}
                    onCreated={() => { setShowNew(false); refresh(); }}
                />
            )}
        </div>
    );
}

// ---- New server migration dialog -------------------------------------------

function NewServerMigrationDialog({ onClose, onCreated }) {
    const navigate = useNavigate();
    const [agents, setAgents] = useState([]);
    const [sourceId, setSourceId] = useState("");
    const [destId, setDestId] = useState("");
    const [name, setName] = useState("");
    const [targetOs, setTargetOs] = useState("linux");
    const [serverRole, setServerRole] = useState("application");
    const [scope, setScope] = useState(new Set(DEFAULT_SCOPE));
    const [conflictMode, setConflictMode] = useState("skip");
    const [preservePerms, setPreservePerms] = useState(true);
    const [includeHidden, setIncludeHidden] = useState(true);
    const [stopServices, setStopServices] = useState(false);
    const [bandwidthCap, setBandwidthCap] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        http.get("/agents").then(({ data }) => setAgents(data || [])).catch(() => {});
    }, []);

    const sources = useMemo(
        () => agents.filter((a) => SERVER_KINDS.has(a.kind || "vm") && (a.role === "source" || a.role === "both" || !a.role)),
        [agents],
    );
    const dests = useMemo(
        () => agents.filter((a) => SERVER_KINDS.has(a.kind || "vm") && (a.role === "destination" || a.role === "both" || !a.role)),
        [agents],
    );
    const srcAgent = agents.find((a) => a.id === sourceId) || null;
    const dstAgent = agents.find((a) => a.id === destId) || null;

    // Auto-infer target OS from the source agent.
    useEffect(() => {
        if (!srcAgent?.os) return;
        const o = srcAgent.os.toLowerCase();
        setTargetOs(o.includes("win") ? "windows" : "linux");
    }, [srcAgent]);

    // Prune OS-specific scope when OS changes.
    useEffect(() => {
        setScope((prev) => {
            const next = new Set(prev);
            for (const s of SCOPE_ITEMS) if (!s.os.includes(targetOs)) next.delete(s.v);
            return next;
        });
    }, [targetOs]);

    const visibleScope = SCOPE_ITEMS.filter((s) => s.os.includes(targetOs));
    const estimateGB = useMemo(
        () => visibleScope.filter((s) => scope.has(s.v)).reduce((a, s) => a + s.sizeGB, 0),
        [scope, visibleScope],
    );
    const estimateBytes = estimateGB * 1024 ** 3;
    const estimateSec = estimateBytes / (100 * 1024 * 1024); // 100 MB/s effective

    const toggleScope = (v) => setScope((prev) => {
        const next = new Set(prev);
        next.has(v) ? next.delete(v) : next.add(v);
        return next;
    });

    const valid = sourceId && destId && sourceId !== destId && scope.size > 0;

    const submit = async () => {
        if (!valid) return;
        setSubmitting(true);
        try {
            const sourcePath = targetOs === "linux" ? "/" : "C:\\";
            const destPath = sourcePath;
            const payload = {
                name: name || `${srcAgent?.name || "src"} → ${dstAgent?.name || "dst"}`,
                source_agent_id: sourceId,
                dest_agent_id: destId,
                source_path: sourcePath,
                destination_path: destPath,
                migration_type: "full",
                mode: "one_shot",
                migration_kind: "server",
                total_files: Math.max(500, Math.round(estimateBytes / (5 * 1024 * 1024))),
                total_bytes: Math.max(1024, estimateBytes),
                options: {
                    bandwidth_limit_mbps: bandwidthCap > 0 ? bandwidthCap : null,
                    retries: 3,
                    retry_backoff_ms: 400,
                    compression: true,
                    encryption: true,
                    enable_versioning: true,
                    mirror_deletes: false,
                },
                server_options: {
                    target_os: targetOs,
                    server_role: serverRole,
                    scope: Array.from(scope),
                    conflict_mode: conflictMode,
                    preserve_permissions: preservePerms,
                    include_hidden: includeHidden,
                    backup_before_overwrite: true,
                    stop_services: stopServices,
                },
            };
            const { data } = await http.post("/jobs", payload);
            toast.success("Server migration queued");
            onCreated();
            navigate(`/jobs/${data.id}`);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            data-testid="srv-new-dialog"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg border border-border bg-surface p-6">
                <div className="mb-4 flex items-center justify-between">
                    <div>
                        <h2 className="font-display text-2xl font-bold">New Server Migration</h2>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            Pick source + destination, OS, role, and scope. Bandwidth cap + advanced options below.
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded-sm border border-border p-1.5 hover:border-foreground/40">
                        <X size={13} />
                    </button>
                </div>

                <div className="space-y-4">
                    <input
                        placeholder="Migration name (auto-filled from agents)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="srv-name-input"
                        className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                    />

                    <div className="grid gap-3 md:grid-cols-2">
                        <ServerPicker label="Source server" agents={sources} value={sourceId} onChange={setSourceId} testid="source" />
                        <ServerPicker label="Destination server" agents={dests} value={destId} onChange={setDestId} testid="dest" />
                    </div>

                    {/* OS + Role row */}
                    <div className="grid gap-3 md:grid-cols-2">
                        <div>
                            <Eyebrow>Target OS</Eyebrow>
                            <div className="mt-1 grid grid-cols-2 gap-2">
                                {[
                                    { v: "linux",   label: "Linux",          sub: "Ubuntu · RHEL · Debian" },
                                    { v: "windows", label: "Windows Server", sub: "2019 · 2022 · 2025" },
                                ].map((o) => (
                                    <button
                                        key={o.v}
                                        type="button"
                                        onClick={() => setTargetOs(o.v)}
                                        data-testid={`srv-os-${o.v}`}
                                        className={[
                                            "rounded border p-3 text-left",
                                            targetOs === o.v
                                                ? "border-[#5b5ef0] bg-[#5b5ef0]/10"
                                                : "border-border hover:border-foreground/30",
                                        ].join(" ")}
                                    >
                                        <div className="text-sm font-semibold">{o.label}</div>
                                        <div className="text-[10px] text-muted-foreground">{o.sub}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Eyebrow>Server role</Eyebrow>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                                {ROLES.map((r) => {
                                    const Icon = r.icon;
                                    const active = serverRole === r.v;
                                    return (
                                        <button
                                            type="button"
                                            key={r.v}
                                            onClick={() => setServerRole(r.v)}
                                            data-testid={`srv-role-${r.v}`}
                                            title={r.note}
                                            className={[
                                                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold",
                                                active
                                                    ? "border-[#5b5ef0] bg-[#5b5ef0]/10 text-[#5b5ef0]"
                                                    : "border-border text-muted-foreground hover:border-foreground/30",
                                            ].join(" ")}
                                        >
                                            <Icon size={11} />
                                            {r.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Scope grid */}
                    <div>
                        <Eyebrow>Scope</Eyebrow>
                        <div className="mt-1 grid gap-1.5 sm:grid-cols-2">
                            {visibleScope.map((item) => {
                                const checked = scope.has(item.v);
                                const Icon = item.icon;
                                return (
                                    <button
                                        type="button"
                                        key={item.v}
                                        onClick={() => toggleScope(item.v)}
                                        data-testid={`srv-scope-${item.v}`}
                                        className={[
                                            "flex items-start gap-2.5 rounded border px-3 py-2 text-left",
                                            checked ? "border-[#5b5ef0] bg-[#5b5ef0]/5" : "border-border hover:border-foreground/30",
                                        ].join(" ")}
                                    >
                                        <div
                                            className={[
                                                "mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border-2",
                                                checked ? "border-[#5b5ef0] bg-[#5b5ef0] text-white" : "border-muted-foreground/50",
                                            ].join(" ")}
                                        >
                                            {checked && <Check size={9} />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5">
                                                <Icon size={11} className={checked ? "text-[#5b5ef0]" : "text-muted-foreground"} />
                                                <span className="text-[12px] font-semibold">{item.label}</span>
                                                {item.advanced && (
                                                    <span className="rounded-sm border border-border px-1 font-mono text-[8px] uppercase text-muted-foreground">
                                                        adv
                                                    </span>
                                                )}
                                            </div>
                                            {(item.note || item.warn) && (
                                                <div className={"mt-0.5 text-[10px] " + (item.warn ? "text-amber-300" : "text-muted-foreground")}>
                                                    {item.warn || item.note}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-2 text-[11px] text-muted-foreground">
                            Estimated transfer: <span className="font-mono text-foreground">{formatBytes(estimateBytes)}</span> ·
                            ETA <span className="font-mono text-foreground">{formatDuration(estimateSec)}</span> @ 100 MB/s effective
                        </div>
                    </div>

                    {/* Advanced collapsible */}
                    <button
                        type="button"
                        onClick={() => setShowAdvanced((v) => !v)}
                        data-testid="srv-advanced-toggle"
                        className="text-xs font-semibold text-[#5b5ef0] hover:underline"
                    >
                        {showAdvanced ? "▾" : "▸"} Advanced options
                    </button>
                    {showAdvanced && (
                        <div className="space-y-3 rounded border border-border bg-background p-3" data-testid="srv-advanced-panel">
                            <div className="grid gap-2 md:grid-cols-2">
                                <Toggle label="Preserve permissions"    sub="UIDs, GIDs, ACLs" checked={preservePerms} onChange={setPreservePerms} testid="srv-opt-perms" />
                                <Toggle label="Include hidden files"    sub="Dotfiles + Win attrs" checked={includeHidden} onChange={setIncludeHidden} testid="srv-opt-hidden" />
                                <Toggle label="Stop services around copy" sub="Stop matching daemon, restart after" checked={stopServices} onChange={setStopServices} testid="srv-opt-stop-svc" tone="warn" />
                            </div>

                            <div>
                                <Eyebrow>Conflict strategy</Eyebrow>
                                <div className="mt-1 grid grid-cols-3 gap-1.5">
                                    {[
                                        { v: "skip",      label: "Skip" },
                                        { v: "overwrite", label: "Overwrite" },
                                        { v: "rename",    label: "Rename" },
                                    ].map((c) => (
                                        <button
                                            key={c.v}
                                            type="button"
                                            onClick={() => setConflictMode(c.v)}
                                            data-testid={`srv-conflict-${c.v}`}
                                            className={[
                                                "rounded border px-2 py-1 text-xs",
                                                conflictMode === c.v
                                                    ? "border-[#5b5ef0] bg-[#5b5ef0]/10 text-[#5b5ef0]"
                                                    : "border-border text-muted-foreground hover:border-foreground/30",
                                            ].join(" ")}
                                        >
                                            {c.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <div className="mb-1 flex items-center justify-between">
                                    <Eyebrow>Bandwidth cap</Eyebrow>
                                    <span
                                        className="rounded border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 font-mono text-[10px] text-blue-200"
                                        data-testid="srv-bandwidth-value"
                                    >
                                        {bandwidthCap === 0 ? "Unlimited" : `${bandwidthCap} MB/s`}
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={0}
                                    max={1000}
                                    step={25}
                                    value={bandwidthCap}
                                    onChange={(e) => setBandwidthCap(parseInt(e.target.value, 10))}
                                    data-testid="srv-bandwidth-range"
                                    className="w-full accent-[#5b5ef0]"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-5 flex items-center justify-end gap-2">
                    <Button variant="ghost" onClick={onClose} data-testid="srv-cancel-btn">Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={!valid || submitting}
                        data-testid="srv-create-btn"
                        className="bg-[#5b5ef0] hover:bg-[#4b4ef0]"
                    >
                        {submitting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Play size={14} className="mr-2" />}
                        Start migration
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ---- Tiny shared bits -------------------------------------------------------

function ServerPicker({ label, agents, value, onChange, testid }) {
    return (
        <div>
            <Eyebrow>{label}</Eyebrow>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={`srv-${testid}-select`}
                className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
            >
                <option value="">Pick an agent…</option>
                {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                        {`${a.name || a.hostname || a.id.slice(0, 8)} — ${a.os || "?"} · ${a.status || "offline"}`}
                    </option>
                ))}
            </select>
        </div>
    );
}

function Toggle({ label, sub, checked, onChange, testid, tone }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            data-testid={testid}
            className={[
                "flex items-start justify-between gap-2 rounded border px-3 py-2 text-left",
                checked
                    ? tone === "warn"
                        ? "border-amber-400/50 bg-amber-500/[0.06]"
                        : "border-[#5b5ef0]/50 bg-[#5b5ef0]/5"
                    : "border-border hover:border-foreground/30",
            ].join(" ")}
        >
            <div className="min-w-0">
                <div className="text-[12px] font-semibold">{label}</div>
                {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
            </div>
            <div
                className={[
                    "mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border-2",
                    checked
                        ? tone === "warn"
                            ? "border-amber-400 bg-amber-400 text-zinc-900"
                            : "border-[#5b5ef0] bg-[#5b5ef0] text-white"
                        : "border-muted-foreground/50",
                ].join(" ")}
            >
                {checked && <Check size={9} />}
            </div>
        </button>
    );
}

function Eyebrow({ children }) {
    return (
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {children}
        </div>
    );
}

function Metric({ icon: Icon, label, value, sublabel, accent }) {
    const accentCls = accent === "blue" ? "text-[#00C2FF]"
        : accent === "emerald" ? "text-emerald-400"
        : "text-foreground";
    const tint = accent === "blue" ? "#00C2FF"
        : accent === "emerald" ? "#22c55e"
        : "#5b5ef0";
    return (
        <div
            className="rounded-xl border border-border bg-surface p-3.5 transition-all hover:border-foreground/20 hover:shadow-sm"
            data-testid={`srv-metric-${label.toLowerCase().replace(/\s/g, "-")}`}
        >
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <span
                    className="inline-flex h-5 w-5 items-center justify-center rounded-md"
                    style={{ background: `${tint}1f`, color: tint }}
                >
                    <Icon size={11} />
                </span>
                {label}
            </div>
            <div className={`mt-1.5 font-display text-xl font-bold tabular-nums ${accentCls}`}>{value}</div>
            {sublabel && <div className="mt-0.5 text-[10px] text-muted-foreground">{sublabel}</div>}
        </div>
    );
}

function StatusPill({ status }) {
    const s = (status || "").toLowerCase();
    const meta = {
        queued:       { cls: "border-blue-400/40 text-blue-200 bg-blue-500/10",       label: "queued" },
        running:      { cls: "border-blue-400/40 text-blue-200 bg-blue-500/10",       label: "running" },
        syncing:      { cls: "border-blue-400/40 text-blue-200 bg-blue-500/10",       label: "syncing" },
        transferring: { cls: "border-blue-400/40 text-blue-200 bg-blue-500/10",       label: "transferring" },
        paused:       { cls: "border-zinc-400/40 text-zinc-200 bg-zinc-500/10",       label: "paused" },
        completed:    { cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", label: "completed" },
        verified:     { cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", label: "verified" },
        failed:       { cls: "border-rose-400/40 text-rose-300 bg-rose-500/10",       label: "failed" },
        cancelled:    { cls: "border-zinc-500/40 text-zinc-400 bg-zinc-700/30",       label: "cancelled" },
    }[s] || { cls: "border-border text-muted-foreground", label: s || "—" };
    return (
        <span
            data-testid={`srv-status-pill-${s}`}
            className={`inline-flex items-center gap-1 rounded border ${meta.cls} px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider`}
        >
            {meta.label}
        </span>
    );
}
